import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar, Users, UserCheck, Eye, ShieldCheck, Crown } from "lucide-react";
import { format } from "date-fns";
import { Link } from "react-router-dom";

interface Test {
  id: string;
  title: string;
  created_at: string;
  is_active: boolean;
  status: string;
  isCollaborator?: boolean;
  permission?: string;
}

interface TestResponse {
  test_id: string;
  count: number;
}

export function TestCardsSection() {
  // Fetch user's own tests
  const { data: ownTests, isLoading: ownTestsLoading } = useQuery({
    queryKey: ["user-tests"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tests")
        .select("id, title, created_at, is_active, status")
        .eq("creator_id", (await supabase.auth.getUser()).data.user?.id)
        .order("created_at", { ascending: false });
      
      if (error) throw error;
      return data as Test[];
    },
  });

  // Fetch collaborator tests
  const { data: collaboratorTests, isLoading: collaboratorTestsLoading } = useQuery({
    queryKey: ["collaborator-tests"],
    queryFn: async () => {
      const user = await supabase.auth.getUser();
      if (!user.data.user?.id) return [];

      // First get accepted collaborations
      const { data: collaborations, error: collaborationsError } = await supabase
        .from("test_collaborators")
        .select("test_id, permission")
        .eq("collaborator_id", user.data.user.id)
        .eq("status", "accepted");

      if (collaborationsError) throw collaborationsError;
      if (!collaborations.length) return [];

      // Then get the test details
      const testIds = collaborations.map(c => c.test_id);
      const { data: tests, error: testsError } = await supabase
        .from("tests")
        .select("id, title, created_at, is_active, status")
        .in("id", testIds)
        .order("created_at", { ascending: false });

      if (testsError) throw testsError;

      // Combine test data with collaboration info
      return tests.map(test => {
        const collaboration = collaborations.find(c => c.test_id === test.id);
        return {
          ...test,
          isCollaborator: true,
          permission: collaboration?.permission
        } as Test;
      });
    },
  });

  // Combine all tests
  const allTests = [...(ownTests || []), ...(collaboratorTests || [])];
  const isLoading = ownTestsLoading || collaboratorTestsLoading;

  // Fetch response counts for all tests
  const { data: responseCounts } = useQuery({
    queryKey: ["test-response-counts", allTests?.map(t => t.id)],
    queryFn: async () => {
      if (!allTests?.length) return {};
      
      const counts: Record<string, number> = {};
      
      // Batch fetch response counts for all tests
      await Promise.all(
        allTests.map(async (test) => {
          const { count, error } = await supabase
            .from("test_responses")
            .select("id", { count: "exact" })
            .eq("test_id", test.id);
          
          if (!error) {
            counts[test.id] = count || 0;
          }
        })
      );
      
      return counts;
    },
    enabled: !!allTests?.length,
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        <h2 className="text-xl font-semibold text-foreground">My Tests</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="p-6 animate-pulse">
              <div className="h-4 bg-muted rounded w-3/4 mb-4"></div>
              <div className="h-3 bg-muted rounded w-1/2 mb-2"></div>
              <div className="h-3 bg-muted rounded w-1/3"></div>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (!allTests?.length) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-foreground mb-2">My Tests</h2>
        <p className="text-muted-foreground">You haven't created any tests yet. Create your first test above!</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-foreground">My Tests</h2>
        <Button variant="link" size="sm" className="text-primary hover:text-primary/80">
          View All
        </Button>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {allTests.map((test) => (
          <Link key={test.id} to={`/test/${test.id}`} className="block">
            <Card className="p-6 hover:shadow-md transition-all cursor-pointer group hover:border-primary/20 h-48 flex flex-col">
              <div className="flex-1 flex flex-col justify-between space-y-4">
                {/* Header */}
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-start gap-2 mb-1">
                    <h3 className="font-semibold text-foreground line-clamp-1 group-hover:text-primary transition-colors flex-1">
                        {test.title}
                      </h3>
                      {test.isCollaborator && (
                        <div className="flex items-center gap-1 mt-0.5">
                          <UserCheck className="h-4 w-4 text-blue-500 flex-shrink-0" />
                          {test.permission === 'view_responses' && (
                            <Eye className="h-3.5 w-3.5 text-gray-500 flex-shrink-0" />
                          )}
                          {test.permission === 'manage_recruitment' && (
                            <ShieldCheck className="h-3.5 w-3.5 text-orange-500 flex-shrink-0" />
                          )}
                          {test.permission === 'full_access' && (
                            <Crown className="h-3.5 w-3.5 text-purple-500 flex-shrink-0" />
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Stats */}
                <div className="space-y-3">
                  <div className="flex items-center text-sm text-muted-foreground">
                    <Calendar className="h-4 w-4 mr-2" />
                    {format(new Date(test.created_at), "MMM d, yyyy")}
                  </div>
                  
                  <div className="flex items-center text-sm text-muted-foreground">
                    <Users className="h-4 w-4 mr-2" />
                    {responseCounts?.[test.id] || 0} applicants
                  </div>
                </div>

                {/* Status */}
                <div className="flex items-center pt-2 border-t border-border">
                  <Badge 
                    variant={test.is_active ? "default" : "secondary"}
                    className={test.is_active ? "bg-green-100 text-green-800 hover:bg-green-100" : "bg-red-50 text-red-700 hover:bg-red-50"}
                  >
                    {test.is_active ? "Active" : "Inactive"}
                  </Badge>
                </div>
              </div>
            </Card>
          </Link>
        ))}
      </div>
    </div>
  );
}